import { NextRequest, NextResponse } from 'next/server';
import { db, pool } from '@/lib/db';
import { verifyPassword, hashPassword } from '@/lib/auth';

// Update user profile (name, phone, picture, password)
export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    const { email, action, data } = body;

    if (!email || !action) {
      return NextResponse.json(
        { error: 'Email and action are required' },
        { status: 400 }
      );
    }

    // Find user
    const user = await db.users.findByEmail(email);
    if (!user) {
      return NextResponse.json(
        { error: 'User not found' },
        { status: 404 }
      );
    }

    // Handle different actions
    switch (action) {
      case 'update_profile':
        return await updateProfile(user.id, data);
      
      case 'update_picture':
        return await updateProfilePicture(user.id, data);
      
      case 'remove_picture':
        return await removeProfilePicture(user.id);
      
      case 'change_password':
        return await changePassword(user, data);
      
      default:
        return NextResponse.json(
          { error: 'Invalid action' },
          { status: 400 }
        );
    }

  } catch (error) {
    console.error('Profile update error:', error);
    return NextResponse.json(
      { error: 'Failed to update profile' },
      { status: 500 }
    );
  }
}

// Update profile (name, phone)
async function updateProfile(userId: string, data: any) {
  const { fullName, phone } = data;

  // Validation
  if (!fullName || !fullName.trim()) {
    return NextResponse.json(
      { error: 'Full name is required' },
      { status: 400 }
    );
  }

  if (!phone || !phone.trim()) {
    return NextResponse.json(
      { error: 'Phone number is required' },
      { status: 400 }
    );
  }

  // Phone validation
  const phoneRegex = /^[0-9\s]{10,}$/;
  if (!phoneRegex.test(phone)) {
    return NextResponse.json(
      { error: 'Please enter a valid phone number' },
      { status: 400 }
    );
  }

  // Update in database
  const updatedUser = await db.users.update(userId, {
    fullName: fullName.trim(),
    phone: phone.trim()
  });

  if (!updatedUser) {
    return NextResponse.json(
      { error: 'Failed to update profile' },
      { status: 500 }
    );
  }

  return NextResponse.json({
    success: true,
    message: 'Profile updated successfully',
    user: {
      id: updatedUser.id,
      fullName: updatedUser.fullName,
      email: updatedUser.email,
      phone: updatedUser.phone,
      profilePicture: updatedUser.profilePicture
    }
  });
}

// Update profile picture
async function updateProfilePicture(userId: string, data: any) {
  const { profilePicture } = data;

  if (!profilePicture) {
    return NextResponse.json(
      { error: 'Profile picture is required' },
      { status: 400 }
    );
  }

  // Update in database
  await db.users.updateProfilePicture(userId, profilePicture);

  // Get updated user
  const updatedUser = await db.users.findById(userId);

  return NextResponse.json({
    success: true,
    message: 'Profile picture updated successfully',
    user: {
      id: updatedUser!.id,
      fullName: updatedUser!.fullName,
      email: updatedUser!.email,
      phone: updatedUser!.phone,
      profilePicture: updatedUser!.profilePicture
    }
  });
}

// Remove profile picture
async function removeProfilePicture(userId: string) {
  // Update in database
  await db.users.updateProfilePicture(userId, null);

  // Get updated user
  const updatedUser = await db.users.findById(userId);

  return NextResponse.json({
    success: true,
    message: 'Profile picture removed successfully',
    user: {
      id: updatedUser!.id,
      fullName: updatedUser!.fullName,
      email: updatedUser!.email,
      phone: updatedUser!.phone,
      profilePicture: updatedUser!.profilePicture
    }
  });
}

// Change password
async function changePassword(user: any, data: any) {
  const { currentPassword, newPassword } = data;

  // Validation
  if (!currentPassword || !newPassword) {
    return NextResponse.json(
      { error: 'Current password and new password are required' },
      { status: 400 }
    );
  }

  if (newPassword.length < 8) {
    return NextResponse.json(
      { error: 'New password must be at least 8 characters' },
      { status: 400 }
    );
  }

  // Verify current password
  const isValidPassword = await verifyPassword(currentPassword, user.passwordHash);
  if (!isValidPassword) {
    return NextResponse.json(
      { error: 'Current password is incorrect' },
      { status: 401 }
    );
  }

  // Hash new password
  const newPasswordHash = await hashPassword(newPassword);

  // Update password in database
  await pool.execute(
    'UPDATE users SET password_hash = ?, updated_at = ? WHERE id = ?',
    [newPasswordHash, new Date(), user.id]
  );

  return NextResponse.json({
    success: true,
    message: 'Password changed successfully'
  });
}
